// ClientPage.jsx (React Client List & Form with DnD Kit + Material UI + Actions + Service Layer)
import React, { useEffect, useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddchartIcon from '@mui/icons-material/Addchart';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ClientDataService from '../../services/client.service';
import VisitDataService from '../../services/visit.service';
import ServiceHistoryDataService from '../../services/serviceHistory.service';
import ServiceDataService from '../../services/service.service';
import { useTranslation } from '../../hooks/useTranslation';
import { formatDateOnly, toDateInputValue } from '../../utils/dateOnly';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function SortableHeaderCell({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'grab',
    background: '#f0f0f0',
    resize: 'horizontal',
    overflow: 'auto',
    minWidth: 100,
  };

  return (
    <TableCell ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </TableCell>
  );
}

export default function Client() {
  const { t, language } = useTranslation();
  const dateLocale = language === 'es' ? 'es-GT' : 'en-US';
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', email: '', custom: {} });
  const [editingClient, setEditingClient] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [visitHistoryOpen, setVisitHistoryOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientVisits, setClientVisits] = useState([]);
  const [newVisitOpen, setNewVisitOpen] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceForm, setServiceForm] = useState({
    service_id: '',
    fecha: '',
    costo: '',
    duration: '2.5',
    description: '',
    status: 'Completed',
    payment_method: ''
  });

  const sensors = useSensors(useSensor(PointerSensor));
  const [nextVisitSort, setNextVisitSort] = useState('asc');

  // Visual indicator for the "next visit" column: overdue / soon / later.
  const nextVisitMeta = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / 86400000);
    let color = 'success';
    if (days < 0) color = 'error';
    else if (days <= 3) color = 'warning';
    return { days, color };
  };

  const filteredClients = useMemo(() => {
    const raw = clientSearch.trim().toLowerCase();
    if (!raw) return clients;
    const terms = raw.split(/\s+/).filter(Boolean);
    return clients.filter((client) => {
      const parts = [
        client.name,
        client.phone,
        client.email,
        ...(client.field_values || []).map((fv) =>
          fv?.value != null && fv.value !== '' ? String(fv.value) : ''
        ),
      ];
      fields.forEach((field) => {
        const fv = (client.field_values || []).find((f) => {
          const id = field.id;
          return (
            f.client_field_id === id ||
            f.field_id === id ||
            f.field?.id === id ||
            parseInt(f.client_field_id, 10) === parseInt(id, 10) ||
            parseInt(f.field_id, 10) === parseInt(id, 10) ||
            parseInt(f.field?.id, 10) === parseInt(id, 10)
          );
        });
        if (fv?.value && field.type === 'date') {
          try {
            parts.push(formatDateOnly(fv.value, dateLocale));
          } catch {
            /* ignore */
          }
        }
      });
      const blob = parts.filter(Boolean).join(' ').toLowerCase();
      return terms.every((term) => blob.includes(term));
    });
  }, [clients, clientSearch, fields, dateLocale]);

  // Order by next visit date; clients with no next visit always go last.
  const sortedClients = useMemo(() => {
    const dir = nextVisitSort === 'desc' ? -1 : 1;
    return [...filteredClients].sort((a, b) => {
      const an = !a.next_visit;
      const bn = !b.next_visit;
      if (an && bn) return 0;
      if (an) return 1;
      if (bn) return -1;
      return (new Date(a.next_visit) - new Date(b.next_visit)) * dir;
    });
  }, [filteredClients, nextVisitSort]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, fieldsRes, servicesRes] = await Promise.all([
        ClientDataService.getClients(),
        ClientDataService.getFields(),
        ServiceDataService.getAll(),
      ]);
     
      
      const clients = clientsRes.data?.data || [];
      const fields = fieldsRes.data || [];
      
      
      setClients(clients);
      setFields(fields);
      setServices(servicesRes.data || []);
      
      // Initialize custom fields form
      const customInit = {};
      fields.forEach(field => {
        customInit[field.id] = '';
      });
      setForm(f => ({ ...f, custom: customInit }));
    } catch (error) {
      console.error('Error loading data:', error);
      // Set empty arrays as fallback
      setClients([]);
      setFields([]);
      setServices([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Check if name is a field ID (custom field) or a standard field
    if (fields.find((f) => f.id.toString() === name)) {
      setForm((prev) => ({
        ...prev,
        custom: { ...prev.custom, [name]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const saveClient = async () => {
    if (!window.confirm('Are you sure you want to save this client?')) return;

    // Transform custom fields to match backend structure
    const allowedFieldIds = new Set((fields || []).map((f) => String(f.id)));
    const fieldValues = [];
    Object.keys(form.custom).forEach(fieldId => {
      if (form.custom[fieldId] !== '') {
        const clientFieldId = parseInt(fieldId, 10);
        if (!Number.isFinite(clientFieldId)) return;
        // Tenant-safety: only send fields that exist in the current tenant’s field list.
        // This prevents 422s like "selected client field does not exist or does not belong to your tenant".
        if (!allowedFieldIds.has(String(clientFieldId))) return;
        fieldValues.push({
          client_field_id: clientFieldId,
          value:
            form.custom[fieldId] === null || form.custom[fieldId] === undefined
              ? null
              : String(form.custom[fieldId]).trim(),
        });
      }
    });

    const rawEmail = typeof form.email === 'string' ? form.email : '';
    const rawPhone = typeof form.phone === 'string' ? form.phone : '';
    const email = rawEmail.trim();
    const phone = rawPhone.trim();

    const payload = {
      name: form.name,
      // Laravel `nullable|email` does NOT accept empty string; convert '' -> null
      phone: phone ? phone : null,
      email: email ? email : null,
      field_values: fieldValues,
    };

    try {
      if (editingClient) {
        await ClientDataService.updateClient(editingClient, payload);
      } else {
        await ClientDataService.createClient(payload);
      }
    } catch (e) {
      console.error('Save client failed:', e?.response?.data || e?.message || e);
      const serverErrors = e?.response?.data?.errors;
      const serverMessage = e?.response?.data?.message;
      if (serverErrors || serverMessage) {
        alert(
          JSON.stringify(
            { message: serverMessage, errors: serverErrors },
            null,
            2
          )
        );
      } else {
        alert(e?.response?.data?.message || e?.message || 'Save failed');
      }
      // Re-throw so the UI error overlay still shows (and we have console details).
      throw e;
    }
    setForm({ name: '', phone: '', email: '', custom: {} });
    setEditingClient(null);
    setFormOpen(false);
    loadData();
  };

  const editClient = (client) => {
    const values = {};
    (client.field_values || []).forEach((v) => {
      const fieldId = v.field?.id ?? v.client_field_id ?? v.field_id;
      if (!fieldId) return;
      const field = fields.find((f) => f.id === fieldId || String(f.id) === String(fieldId));
      const raw = v.value ?? '';
      values[fieldId] = field?.type === 'date' ? toDateInputValue(raw) : raw;
    });
    console.log('esto edita', values);
    fields.forEach((f) => {
      if (!values[f.id]) values[f.id] = '';
    });
    console.log("cliente editando", client);
    setForm({
      name: client.name,
      phone: client.phone,
      email: client.email,
      custom: values
    });
    setEditingClient(client.id);
    setFormOpen(true);
  };

  const deleteClient = async () => {
    if (editingClient) {
      await ClientDataService.deleteClient(editingClient);
      setEditingClient(null);
      loadData();
      setConfirmOpen(false);
    }
  };

  const viewVisitHistory = async (client) => {
    setSelectedClient(client);
    try {
      const visitsRes = await VisitDataService.getVisitsByClient(client.id);
      setClientVisits(visitsRes.data);
      setVisitHistoryOpen(true);
    } catch (error) {
      console.error('Error fetching visit history:', error);
    }
  };

  const openNewVisitForm = (client) => {
    setSelectedClient(client);
    setNewVisitOpen(true);
  };

  const openServiceModal = (client) => {
    setSelectedClient(client);
    const newServiceForm = {
      service_id: '',
      fecha: new Date().toISOString().split('T')[0],
      costo: '',
    };
    setServiceForm(newServiceForm);
    console.log('Available services:', services);
    console.log('Service form initialized:', newServiceForm);
    setServiceModalOpen(true);
  };

  const handleServiceFormChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for costo field to limit decimal places
    if (name === 'costo') {
      // Remove any non-numeric characters except decimal point
      let cleanValue = value.replace(/[^0-9.]/g, '');
      
      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        cleanValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
      
      setServiceForm(prev => ({
        ...prev,
        [name]: cleanValue
      }));
    } else {
      setServiceForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const saveServiceHistory = async () => {
    if (!serviceForm.service_id || !serviceForm.fecha || !serviceForm.costo) {
      alert('Please fill in all required fields: Service Type');
      return;
    }

    if (serviceForm.service_id === '') {
      alert('Please select a service type');
      return;
    }

    // Validate that costo has at most 2 decimal places
    if (serviceForm.costo && !/^\d+(\.\d{1,2})?$/.test(serviceForm.costo)) {
      alert('Value must have at most 2 decimal places (e.g., 100.50)');
      return;
    }

    try {
      const serviceData = {
        service_id: parseInt(serviceForm.service_id),
        client_id: selectedClient.id,
        fecha: serviceForm.fecha,
        costo: parseFloat(serviceForm.costo)
      };

      await ServiceHistoryDataService.create(serviceData);
      setServiceModalOpen(false);
      alert('Service history saved successfully!');
    } catch (error) {
      console.error('Error saving service history:', error);
      alert('Error saving service history. Please try again.');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = fields.findIndex((f) => f.name === active.id);
      const newIndex = fields.findIndex((f) => f.name === over.id);
      const reordered = arrayMove(fields, oldIndex, newIndex);
      setFields(reordered);
      await ClientDataService.updateFieldOrder(
        reordered.map((f, i) => ({ ...f, order: i }))
      );
    }
  };

  const renderCustomField = (field) => {
    const fieldValue = form.custom?.[field.id.toString()] ?? '';
    
    switch (field.type) {
      case 'textarea':
        return (
          <TextField
            key={field.id}
            name={field.id.toString()} 
            label={field.label}
            value={fieldValue}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
            required={field.required === 1}
          />
        );
      
      case 'number':
        return (
          <TextField
            key={field.id}
            name={field.id.toString()}
            label={field.label}
            value={fieldValue}
            onChange={handleChange}
            type="number"
            fullWidth
            required={field.required === 1}
          />
        );
      
      case 'email':
        return (
          <TextField
            key={field.id}
            name={field.id.toString()}
            label={field.label}
            value={fieldValue}
            onChange={handleChange}
            type="email"
            fullWidth
            required={field.required === 1} //need to change to true or false
          />
        );
      
      case 'url':
        return (
          <TextField
            key={field.id}
            name={field.id.toString()}
            label={field.label}
            value={fieldValue}
            onChange={handleChange}
            type="url"
            fullWidth
            required={field.required === 1}
          />
        );
      
      case 'date':
        return (
          <TextField
            key={field.id}
            name={field.id.toString()}
            label={field.label}
            value={toDateInputValue(fieldValue)}
            onChange={handleChange}
            type="date"
            fullWidth
            required={field.required === 1}
            InputLabelProps={{
              shrink: true,
            }}
          />
        );
      
      case 'time':
        return (
          <TextField
            key={field.id}
            name={field.id.toString()}
            label={field.label}
            value={fieldValue}
            onChange={handleChange}
            type="time"
            fullWidth
            required={field.required === 1}
            InputLabelProps={{
              shrink: true,
            }}
          />
        );
      
      case 'select':
        const options = field.options ? field.options.split(',').map(opt => opt.trim()) : [];
        return (
          <FormControl key={field.id} fullWidth required={field.required === 1}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              name={field.id.toString()}
              value={fieldValue}
              onChange={handleChange}
              label={field.label}
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      default: // text
        return (
          <TextField
            key={field.id}
            name={field.id.toString()}
            label={field.label}
            value={fieldValue}
            onChange={handleChange}
            fullWidth
            required={field.required === 1}
          />
        );
    }
  };

  const formatFieldValue = (field, value) => {
    if (!value) return '';
    
    switch (field.type) {
      case 'date':
        try {
          return formatDateOnly(value, dateLocale);
        } catch {
          return value;
        }
      case 'time':
        return value;
      case 'number':
        return typeof value === 'number' ? value.toString() : value;
      case 'url':
        return value.length > 30 ? `${value.substring(0, 30)}...` : value;
      case 'textarea':
        return value.length > 50 ? `${value.substring(0, 50)}...` : value;
      default:
        return value;
    }
  };

  const colSpan = 6 + fields.length;

  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>
        {t('clients.title')}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <TextField
            size="small"
            placeholder={t('clients.searchPlaceholder')}
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            sx={{ flex: '1 1 280px', maxWidth: { sm: 480 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: clientSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t('clients.clearSearch')}
                    onClick={() => setClientSearch('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {hasPermission('clients.create') && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setForm({ name: '', phone: '', email: '', custom: {} });
                setEditingClient(null);
                setFormOpen(true);
              }}
              startIcon={<AddIcon />}
              sx={{ flexShrink: 0 }}
            >
              {t('clients.addClient')}
            </Button>
          )}
        </Box>
        {clients.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {t('clients.showingClients', {
              filtered: filteredClients.length,
              total: clients.length,
            })}
          </Typography>
        )}
        {fields.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mt={1.5}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              {t('clients.customFields')}:
            </Typography>
            {fields.map((field) => (
              <Chip
                key={field.id}
                size="small"
                variant="outlined"
                label={`${field.label}`}
              />
            ))}
          </Box>
        )}
      </Paper>

      <Table>
        <TableHead>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fields.map((f) => f.label)} strategy={horizontalListSortingStrategy}>
              <TableRow>
                <TableCell>{t ('clients.clientName')}</TableCell>
                <TableCell>{t ('clients.clientPhone')}</TableCell>
                <TableCell>{t ('clients.clientEmail')}</TableCell>
                <TableCell>{t('clients.lastVisit')}</TableCell>
                <TableCell
                  sortDirection={nextVisitSort}
                  sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => setNextVisitSort((s) => (s === 'asc' ? 'desc' : 'asc'))}
                >
                  <TableSortLabel active direction={nextVisitSort}>
                    {t('clients.nextVisit')}
                  </TableSortLabel>
                </TableCell>
                {fields.map((field) => (
                  <SortableHeaderCell key={field.id} id={field.label}>
                    {field.label}
                  </SortableHeaderCell>
                ))}
                <TableCell align="left">{t ('clients.actionsfield')}</TableCell>
              </TableRow>
            </SortableContext>
          </DndContext>
        </TableHead>
        <TableBody>
          {filteredClients.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                <Typography color="text.secondary">
                  {clients.length === 0
                    ? t('clients.noClientsFound')
                    : t('clients.noSearchResults')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedClients.map((client) => {
            const nextMeta = nextVisitMeta(client.next_visit);
            return (
              <TableRow key={client.id}>
                <TableCell>{client.name}</TableCell>
                <TableCell>{client.phone}</TableCell>
                <TableCell>{client.email}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {client.last_visit ? formatDateOnly(client.last_visit, dateLocale) : '—'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {client.next_visit ? (
                    <Chip
                      size="small"
                      color={nextMeta?.color || 'default'}
                      variant="outlined"
                      label={formatDateOnly(client.next_visit, dateLocale)}
                    />
                  ) : (
                    <Typography component="span" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                {fields.map((field) => {
                  const fieldValue = client.field_values?.find((fv) => {
                    const matches =
                      fv.client_field_id === field.id ||
                      fv.field_id === field.id ||
                      fv.field?.id === field.id ||
                      parseInt(fv.client_field_id, 10) === parseInt(field.id, 10) ||
                      parseInt(fv.field_id, 10) === parseInt(field.id, 10) ||
                      parseInt(fv.field?.id, 10) === parseInt(field.id, 10);
                    return matches;
                  });

                  return (
                    <TableCell key={field.id}>
                      {fieldValue ? formatFieldValue(field, fieldValue.value) : ''}
                    </TableCell>
                  );
                })}
                <TableCell align="left">
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    {hasPermission('clients.edit') && (
                      <IconButton 
                        color="primary" 
                        onClick={() => editClient(client)}
                        title="Edit Client"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {hasPermission('service-history.create') && (
                      <IconButton 
                        color="info" 
                        onClick={() => openServiceModal(client)}
                        title="Add Service"
                      >
                        <AddchartIcon />
                      </IconButton>
                    )}
                    {hasPermission('clients.appointments.quick') && hasPermission('calendar.view') && (
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/calendar?clientId=${client.id}`)}
                        title="Schedule Appointment"
                      >
                        <CalendarTodayIcon />
                      </IconButton>
                    )}
                    {hasPermission('visits.view') && (
                      <IconButton
                        color="secondary"
                        onClick={() => navigate(`/visits?clientId=${client.id}&clientName=${encodeURIComponent(client.name || '')}`)}
                        title="Register Visit"
                      >
                        <EventNoteIcon />
                      </IconButton>
                    )}
                    {hasPermission('cleaning-details.view') && (
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/cleaning-details?clientId=${client.id}`)}
                        title={t('clients.openCleaningDetails')}
                      >
                        <HomeWorkIcon />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })
          )}
        </TableBody>
      </Table>

      {/* Client Form Modal */}
      <Dialog 
        open={formOpen} 
        onClose={() => setFormOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editingClient ? t ('clients.editClient') : t ('clients.addClient')}
            <IconButton onClick={() => setFormOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label={t ('clients.clientName')}
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label={t ('clients.clientPhone')}
                  value={form.phone}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="email"
                  label={t ('clients.clientEmail')}
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              {fields.map((field) => (
                <Grid item xs={12} sm={6} key={field.id}>
                  {renderCustomField(field)}
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>
          {t ('clients.cancel')}
          </Button>
        
          <Button 
            variant="contained" 
            color="primary" 
            onClick={saveClient}
            startIcon={<SaveIcon />}
          >
            {editingClient ? t ('clients.cupdate') : t ('clients.csave')} {t ('clients.client')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t ('clients.condele')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
          {t ('clients.msgdele')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t ('clients.cancel')}</Button>
          <Button onClick={deleteClient} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Visit History Dialog */}
      <Dialog 
        open={visitHistoryOpen} 
        onClose={() => setVisitHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
        {t ('clients.vhistory')}- {selectedClient?.name}
        </DialogTitle>
        <DialogContent>
          {clientVisits.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
               {t ('clients. msjnovisit')}
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Motivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientVisits.map((visit) => (
                  <TableRow key={visit.id}>
                    <TableCell>
                      {new Date(visit.schedule).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{visit.title}</TableCell>
                    <TableCell>{visit.type?.name}</TableCell>
                    <TableCell>{visit.status?.name}</TableCell>
                    <TableCell>{visit.motivo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisitHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* New Visit Dialog */}
      <Dialog 
        open={newVisitOpen} 
        onClose={() => setNewVisitOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Create New Visit - {selectedClient?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This will open the visits page where you can create a new visit for this client.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewVisitOpen(false)}>{t ('clients.cancel')}</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              setNewVisitOpen(false);
              // You can add navigation logic here to go to visits page
              // or open a visit creation form
            }}
          >
            Go to Visits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Service Modal */}
      <Dialog 
        open={serviceModalOpen} 
        onClose={() => setServiceModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
          {t ('clients.addservadvform')}
            <IconButton onClick={() => setServiceModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="service-type-label"></InputLabel> 
                <Select
                  name="service_id"
                  value={serviceForm.service_id || ''}
                  onChange={handleServiceFormChange}
                  labelId="service-type-label"
                  label="Service Type"
                  displayEmpty

                >
                  <MenuItem value=""  disabled>
                    <em style={{ color: '#999' }}>{t ('clients.selecservice')}</em>
                  </MenuItem>
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                name="fecha"
                label={t ('clients.datefield')}
                type="date"
                value={serviceForm.fecha}
                onChange={handleServiceFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarTodayIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="costo"
                label={t ('clients.value')}
                type="number"
                value={serviceForm.costo}
                onChange={handleServiceFormChange}
                fullWidth
                inputProps={{
                  step: 0.01,
                  min: 0,
                  max: 999999.99
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{require('../../utils/currencyUtils').getCurrencySymbolSync()}</InputAdornment>,
                }}
                placeholder="0.00"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceModalOpen(false)}>
          {t ('clients.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={saveServiceHistory}
            startIcon={<SaveIcon />}
          >
            {t ('clients.sservice')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
